<template>
  <div>
    <div class="mb-3">
      <b-button v-b-modal.help-modal>Help</b-button>
    </div>

    <template v-if="isLoadedNormal">
      <p class="mb-3">
        Events: <span :class="eventAccessClass">{{eventAccessStatus | getEventAccessStatusName}}</span>
      </p>

      <b-button :to="{name: 'player_info'}">Cancel</b-button>

      <b-button
        v-if="isResetButtonVisible"
        @click="resetWithConfirmation"
        variant="danger"
      >
        Reset
      </b-button>

      <b-button
        v-if="isBlockButtonVisible"
        @click="blockWithConfirmation"
        variant="primary"
      >
        Block
      </b-button>

      <b-button
        v-if="isUnblockButtonVisible"
        @click="unblockWithConfirmation"
        variant="primary"
      >
        Unblock
      </b-button>
    </template>

    <loading-indicator v-if="isLoading"/>

    <b-modal id="help-modal" title="Справка" ok-only ok-title="Close">
      <div class="help">
        <p>
          Серверный флаг (который влияет на доступ игрока к&nbsp;событиям) может быть в одном из трех состояний.
        </p>
        <ul>
          <li>открыто (open)</li>
          <li>заблокировано (blocked)</li>
          <li>разблокировано (unblocked)</li>
        </ul>

        <p>
          Состояние «открыто» (open) — начальное состояние.
          У всех новых игроков северный флаг находится в этом состоянии.
          При автоматической блокировке доступа к&nbsp;событиям сервер проверяет игроков,
          у&nbsp;которых серверный флаг находится в состоянии «открыто» (open).
        </p>

        <p>
          Состояние «заблокировано» (blocked) означает,
          что у&nbsp;игрока нет доступа к событиям.
        </p>

        <p>
          Состояние «разблокировано» (unblocked) означает, что пользователь инсайдера вручную разблокировал игрока.
          При автоматической блокировке доступа к&nbsp;событиям сервер игнорирует игроков,
          у&nbsp;которых серверный флаг находится в состоянии «разблокировано» (unblocked).
          Т.е.&nbsp;для такого игрока доступ к событиям не будет заблокирован автоматически
          (даже если клиент выставит ему новый флаг о подозрительной деятельности).
        </p>
      </div>
    </b-modal>

  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import { EVENT_ACCESS_STATUS__OPEN      } from '../model/event_access.js';
import { EVENT_ACCESS_STATUS__BLOCKED   } from '../model/event_access.js';
import { EVENT_ACCESS_STATUS__UNBLOCKED } from '../model/event_access.js';
import { getEventAccessStatusName       } from '../model/event_access.js';
import { getEventAccessStatusClass      } from '../model/event_access.js';
import { isEventAccessCanBeOpen         } from '../model/event_access.js';
import { isEventAccessCanBeBlocked      } from '../model/event_access.js';
import { isEventAccessCanBeUnblocked    } from '../model/event_access.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import LoadingIndicator from './LoadingIndicator.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'PlayerEventAccessStatusEdit',

  components: {
    LoadingIndicator,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      loadingStatus: LOADING_STATUS__INIT,
      eventAccessStatus: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {Object} */
    eventAccessClass() {
      return getEventAccessStatusClass(this.eventAccessStatus);
    },

    /** @returns {boolean} */
    isResetButtonVisible() {
      return isEventAccessCanBeOpen(this.eventAccessStatus);
    },

    /** @returns {boolean} */
    isBlockButtonVisible() {
      return isEventAccessCanBeBlocked(this.eventAccessStatus);
    },

    /** @returns {boolean} */
    isUnblockButtonVisible() {
      return isEventAccessCanBeUnblocked(this.eventAccessStatus);
    },
  },

  created() {
    this.loadEventAccessStatus();
  },

  methods: {
    loadEventAccessStatus() {    
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/player/${this.playerId}/event_access_status`;
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.eventAccessStatus = data.eventAccessStatus;
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Block event access
    //-------------------------------------------------------------------------

    blockWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      this.confirmEventAccessBlock(
        () => {
          this.blockEventAccess();
        },
      );
    },

    /** @param {ConfirmHandle} onConfirmed */
    confirmEventAccessBlock(onConfirmed) {
      const messageTemplate = 'Are you sure you want to block event access for this player {env}?';

      confirmAction(
        this,
        messageTemplate,
        'Block',
        onConfirmed,
      );
    },

    blockEventAccess() {
      const playerId = this.playerId;
      const url = `/player/${playerId}/block_event_access`;

      HTTP.post(url)
      .then(() => {
        this.$emit('event-access-status-updated', EVENT_ACCESS_STATUS__BLOCKED);
        notifier.showSuccessAfterTransition('Event access blocked.');
        this.$router.push({name: 'player_info'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Reset event access
    //-------------------------------------------------------------------------

    resetWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      this.confirmEventAccessReset(
        () => {
          this.resetEventAccess();
        }
      );
    },

    /** @param {ConfirmHandle} onConfirmed */
    confirmEventAccessReset(onConfirmed) {
      const messageTemplate = 'Are you sure you want to reset event access for this player {env}?';

      confirmAction(
        this,
        messageTemplate,
        'Reset',
        onConfirmed,
      );
    },

    resetEventAccess() {
      const playerId = this.playerId;
      const url = `/player/${playerId}/reset_event_access`;

      HTTP.post(url)
      .then(() => {
        this.$emit('event-access-status-updated', EVENT_ACCESS_STATUS__OPEN);
        notifier.showSuccessAfterTransition('Reset completed.');
        this.$router.push({name: 'player_info'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Unblock event access
    //-------------------------------------------------------------------------

    unblockWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      this.confirmEventAccessUnblock(
        () => {
          this.unblockEventAccess();
        }
      );
    },

    /** @param {ConfirmHandle} onConfirmed */
    confirmEventAccessUnblock(onConfirmed) {
      const messageTemplate = 'Are you sure you want to unblock event access for this player {env}?';

      confirmAction(
        this,
        messageTemplate,
        'Unblock',
        onConfirmed,
      );
    },

    unblockEventAccess() {
      const playerId = this.playerId;
      const url = `/player/${playerId}/unblock_event_access`;

      HTTP.post(url)
      .then(() => {
        this.$emit('event-access-status-updated', EVENT_ACCESS_STATUS__UNBLOCKED);
        notifier.showSuccessAfterTransition('Event access unblocked.');
        this.$router.push({name: 'player_info'});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },
  },

  filters: {
    getEventAccessStatusName,
  },
};
</script>
